import {
  LegacyTextChatMessage,
  TextChatMessageProps,
  TextChatShellMessage,
} from "./TextChatMessage.types";
import {
  DEFAULT_TEXT_CHAT_BODY_RENDERERS,
  TextMessageBody,
} from "./TextChatMessageBodyRenderers";
import TextChatMessageShell from "./TextChatMessageShell";

const isLegacyMessage = (
  message: TextChatMessageProps["message"],
): message is LegacyTextChatMessage => "messageId" in message;

const normalizeLegacyMessage = (
  message: LegacyTextChatMessage,
): TextChatShellMessage => ({
  id: message.messageId,
  type: message.realTimeBooking
    ? "booking"
    : message.voice
      ? "voice"
      : message.userId === "System"
        ? "system"
        : "text",
  senderKind:
    message.userId === "System"
      ? "system"
      : message.sender === "user"
        ? "currentUser"
        : "otherUser",
  text: message.text,
  date: message.date || new Date(),
  attachments: [],
  voice: message.voice,
  replyToMessage: message.replyToMessage,
  reactions: message.reactions || [],
  readBy: message.readBy || [],
  deliveryState: message.deliveryState,
  isLocalOnly: message.isLocalOnly,
  onVoicePlaybackError: message.onVoicePlaybackError,
  realTimeBooking: message.realTimeBooking,
  editedAt: message.editedAt,
  updatedAt: message.updatedAt,
  isEdited: message.isEdited,
  userId: message.userId,
  files: message.files,
});

const TextChatMessage = ({
  message,
  name,
  surname,
  showName,
  style,
  reactOnMessage,
  isDarkChat,
  isChatForVideoSession,
  groupChatMembers,
  actions,
  bodyRenderers,
  resolvedFiles,
  focusTargetId,
  highlighted,
  onVoicePlaybackError,
  onNavigateToMessage,
}: TextChatMessageProps) => {
  const normalizedMessage = isLegacyMessage(message)
    ? normalizeLegacyMessage(message)
    : {
        ...message,
        userId: message.userId || message.sender?.id,
      };

  const bodyRenderer =
    bodyRenderers?.[normalizedMessage.type] ||
    DEFAULT_TEXT_CHAT_BODY_RENDERERS[normalizedMessage.type] ||
    TextMessageBody;
  const body = bodyRenderer({
    message: normalizedMessage,
    files: resolvedFiles,
    isUserMessage: normalizedMessage.senderKind === "currentUser",
    name,
    surname,
    showName,
    onMediaInteraction:
      normalizedMessage.onVoicePlaybackError || onVoicePlaybackError,
    onNavigateToMessage,
  });

  return (
    <TextChatMessageShell
      message={normalizedMessage}
      body={body}
      actions={actions}
      name={name}
      surname={surname}
      style={style}
      reactOnMessage={reactOnMessage}
      isDarkChat={isDarkChat}
      isChatForVideoSession={isChatForVideoSession}
      groupChatMembers={groupChatMembers}
      deliveryState={normalizedMessage.deliveryState}
      focusTargetId={focusTargetId}
      highlighted={highlighted}
    />
  );
};

export default TextChatMessage;
